import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert as MuiAlert, Autocomplete, Box, Card, CardContent, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, Divider, Grid, InputAdornment, Paper, Snackbar, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, Button } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import PercentIcon from "@mui/icons-material/Percent";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import axios from "src/utils/axios";
import Alert from "src/components/Alert";
import * as XLSX from "xlsx";

const MONTH_PASCAL = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];
const capField = (f) => f.charAt(0).toUpperCase() + f.slice(1);

// Tema
const H_DARK = "#004d40";
const H_MID = "#00695c";
const R_EVEN = "#ffffff";
const R_ODD = "#e0f2f1";

// Aylar
const MONTHS = [
    { field: "jan", label: "Ocak" }, { field: "feb", label: "Şubat" },
    { field: "mar", label: "Mart" }, { field: "apr", label: "Nisan" },
    { field: "may", label: "Mayıs" }, { field: "jun", label: "Haziran" },
    { field: "jul", label: "Temmuz" }, { field: "aug", label: "Ağustos" },
    { field: "sep", label: "Eylül" }, { field: "oct", label: "Ekim" },
    { field: "nov", label: "Kasım" }, { field: "dec", label: "Aralık" },
];

const IDX_MONTHS = [
    { field: "janIdx", label: "Ocak" }, { field: "febIdx", label: "Şubat" },
    { field: "marIdx", label: "Mart" }, { field: "aprIdx", label: "Nisan" },
    { field: "mayIdx", label: "Mayıs" }, { field: "junIdx", label: "Haziran" },
    { field: "julIdx", label: "Temmuz" }, { field: "augIdx", label: "Ağustos" },
    { field: "sepIdx", label: "Eylül" }, { field: "octIdx", label: "Ekim" },
    { field: "novIdx", label: "Kasım" }, { field: "decIdx", label: "Aralık" },
];

// Excel kolon haritaları
const normH = (s) =>
    String(s).trim().toLocaleLowerCase("tr-TR").replace(/^\d{4}\s*/, "").trim();

const DIM_COL_MAP = [
    { headers: ["ürün (3. seviye)", "ürün 3. seviye", "3. seviye", "product level 3"], field: "productLevel3" },
    { headers: ["ürün (4. seviye)", "ürün 4. seviye", "4. seviye", "product level 4"], field: "productLevel4" },
    { headers: ["marka", "brand"], field: "brand" },
    { headers: ["ankastre/solo", "ankastre solo", "ankastre-solo"], field: "builtInSolo" },
    { headers: ["temin yeri", "supply location"], field: "supplyLocation" },
];

const toNum = (v) => {
    const n = parseFloat(String(v).replace(",", ".").replace("%", "").trim());
    return isNaN(n) ? 0 : n;
};

// Oran/zam kolonları: kullanıcı "5" (yüzde) ya da "0,05" (kesir) girebilir.
// Mevcut Montaj Oranı kolonuyla aynı normalize kuralı: <=1 ise yüzdeye çevir.
const toPercentNumber = (v) => {
    const n = parseFloat(String(v).replace("%", "").replace(",", ".").trim());
    if (isNaN(n)) return null;
    return n <= 1 ? +(n * 100).toFixed(4) : n;
};

const SALES_COL_MAP = [
    ...DIM_COL_MAP,
    ...MONTHS.map((m) => ({
        headers: [m.label.toLocaleLowerCase("tr-TR")],
        field: m.field,
        transform: toNum,
    })),
];

const ACTUAL_COL_MAP = [
    ...DIM_COL_MAP,
    { headers: ["toplam adet", "toplam", "adet"], field: "totalQuantity", transform: toNum },
    { headers: ["kaç aylık", "kaç aylik", "ay sayısı", "ay"], field: "monthCount", transform: toNum },
];

const ASSEMBLY_COL_MAP = [
    { headers: ["ürün (3. seviye)", "ürün 3. seviye", "3. seviye"], field: "productLevel3" },
    { headers: ["ürün (4. seviye)", "ürün 4. seviye", "4. seviye"], field: "productLevel4" },
    {
        headers: ["montaj oranı", "montaj oran", "oran"],
        field: "assemblyRate",
        transform: (v) => {
            const n = parseFloat(String(v).replace("%", "").replace(",", ".").trim());
            if (isNaN(n)) return null;
            return n <= 1 ? +(n * 100).toFixed(4) : n;
        },
    },
];

// Mevsimsel indeks için aynı kolonlar ama farklı isimlendirme ve dönüşümle
const SEASONAL_COL_MAP = [
    ...DIM_COL_MAP,
    ...IDX_MONTHS.map((m) => ({
        headers: [
            `indeks ${m.label.toLocaleLowerCase("tr-TR")}`,
            `${m.label.toLocaleLowerCase("tr-TR")} indeks`,
            m.label.toLocaleLowerCase("tr-TR"),
        ],
        field: m.field,
        transform: toNum,
    })),
];

// Planlanan Yıllar Listesi
const _cy = new Date().getFullYear();
const YEARS = Array.from({ length: 7 }, (_, i) => _cy - 5 + i);

// Sütun Tanımları
const DIM5 = [
    { field: "productLevel3", label: "Ürün (3. Seviye)", width: 200, left: 0, sticky: true },
    { field: "productLevel4", label: "Ürün (4. Seviye)", width: 260, left: 200, sticky: true },
    { field: "brand", label: "Marka", width: 240, left: 460, sticky: true },
    { field: "builtInSolo", label: "Ankastre/Solo", width: 120, left: 700, sticky: true },
    { field: "supplyLocation", label: "Temin Yeri", width: 120, left: 820, sticky: true, stickyEnd: true },
];

const SALES_QTY_COLS = [
    ...DIM5,
    ...MONTHS.map((m) => ({ field: m.field, label: m.label, width: 90, align: "right", numeric: true })),
    {
        field: "_total", label: "Toplam", width: 110, align: "right", bold: true,
        computed: (row) => { const t = MONTHS.reduce((s, m) => s + Number(row[m.field] || 0), 0); return t || null; },
    },
];

const ACTUAL_QTY_COLS = [
    ...DIM5,
    { field: "totalQuantity", label: "Toplam Adet", width: 130, align: "right", numeric: true },
    { field: "monthCount", label: "Kaç Aylık", width: 110, align: "right", numeric: true },
];

const ASSEMBLY_RATE_COLS = [
    { field: "productLevel3", label: "Ürün (3. Seviye)", width: 340 },
    { field: "productLevel4", label: "Ürün (4. Seviye)", width: 420 },
    { field: "assemblyRate", label: "Montaj Oranı (%)", width: 180, align: "right", numeric: true },
];

const SEASONAL_IDX_COLS = [
    ...DIM5,
    ...IDX_MONTHS.map((m) => ({ field: m.field, label: m.label, width: 90, align: "right", numeric: true })),
];

const CALC_ASSEMBLY_COLS = [
    ...DIM5,
    ...MONTHS.map((m) => ({ field: m.field, label: m.label, width: 90, align: "right", numeric: true })),
    {
        field: "_total", label: "Toplam", width: 110, align: "right", bold: true,
        computed: (row) => { const t = MONTHS.reduce((s, m) => s + Number(row[m.field] || 0), 0); return t || null; },
    },
];

// SAP-BPC Oranı: Aylık Montaj Adedi / Aylık Satış Adedi (fiili bilinen aylar boş bırakılır)
const SAP_BPC_RATE_COLS = [
    ...DIM5,
    ...MONTHS.map((m) => ({ field: m.field, label: m.label, width: 100, align: "right", percent: true })),
];

// Montaj Birim Maliyeti girdisi: ürün anahtarı Ürün(3)+Ürün(4)+Ankastre/Solo
const DIM3 = [
    { field: "productLevel3", label: "Ürün (3. Seviye)", width: 200, left: 0, sticky: true },
    { field: "productLevel4", label: "Ürün (4. Seviye)", width: 260, left: 200, sticky: true },
    { field: "builtInSolo", label: "Ankastre/Solo", width: 130, left: 460, sticky: true, stickyEnd: true },
];

const UNIT_COST_COL_MAP = [
    ...DIM_COL_MAP.filter((c) => c.field === "productLevel3" || c.field === "productLevel4" || c.field === "builtInSolo"),
    { headers: ["işçilik taban", "işçilik taban tutar"], field: "laborBase", transform: toNum },
    ...MONTHS.map((m) => ({
        headers: [`işçilik ${m.label.toLocaleLowerCase("tr-TR")} zam`],
        field: `labor${capField(m.field)}Rate`,
        transform: toPercentNumber,
    })),
    { headers: ["554 taban", "554 taban tutar", "tutar 554 taban"], field: "amt554Base", transform: toNum },
    ...MONTHS.map((m) => ({
        headers: [`554 ${m.label.toLocaleLowerCase("tr-TR")} zam`],
        field: `amt554${capField(m.field)}Rate`,
        transform: toPercentNumber,
    })),
    { headers: ["keşif tutarı", "keşif", "kesif tutari"], field: "surveyAmount", transform: toNum },
    ...MONTHS.map((m) => ({
        headers: [`malzeme ${m.label.toLocaleLowerCase("tr-TR")}`],
        field: `mat${capField(m.field)}`,
        transform: toNum,
    })),
];

const UNIT_COST_INPUT_COLS = [
    ...DIM3,
    { field: "laborBase", label: "İşçilik Taban", width: 130, align: "right", numeric: true },
    ...MONTHS.map((m) => ({ field: `labor${capField(m.field)}Rate`, label: `İşç. ${m.label} Zam (%)`, width: 130, align: "right", numeric: true })),
    { field: "amt554Base", label: "554 Taban", width: 130, align: "right", numeric: true },
    ...MONTHS.map((m) => ({ field: `amt554${capField(m.field)}Rate`, label: `554 ${m.label} Zam (%)`, width: 130, align: "right", numeric: true })),
    { field: "surveyAmount", label: "Keşif Tutarı", width: 130, align: "right", numeric: true },
    ...MONTHS.map((m) => ({ field: `mat${capField(m.field)}`, label: `Malzeme ${m.label}`, width: 120, align: "right", numeric: true })),
];

const UNIT_COST_CALC_COLS = [
    ...DIM5,
    ...MONTHS.map((m) => ({ field: m.field, label: `${m.label} Birim Maliyet`, width: 130, align: "right", numeric: true })),
    ...MONTHS.map((m) => ({ field: `gider_${m.field}`, label: `${m.label} Montaj Gideri`, width: 140, align: "right", numeric: true })),
];

// Başarı popup'ında Montaj Birim Maliyeti özeti - Gider sütunları olmadan, popup'ı kompakt tutar.
const UNIT_COST_SUMMARY_COLS = [
    ...DIM5,
    ...MONTHS.map((m) => ({ field: m.field, label: m.label, width: 110, align: "right", numeric: true })),
];

// Dışa aktarılan yardımcılar
export const getRowTotal = (row) => {
    return MONTHS.reduce((sum, m) => sum + Number(row[m.field] || 0), 0);
}

export const getMissingRequiredFieldCount = (rows) => {
    return rows.filter((r) => !r.productLevel3 || !r.productLevel4 || !r.brand || !r.builtInSolo || !r.supplyLocation).length;
};

// SummaryCard
function SummaryCard({ title, value, icon, helper, color = "primary.main" }) {
    return (
        <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, height: "100%" }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ width: 44, height: 44, borderRadius: 2, display: "grid", placeItems: "center", bgcolor: "grey.100", color, flexShrink: 0 }}>
                        {icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" display="block">{title}</Typography>
                        <Typography variant="h6" fontWeight={800} lineHeight={1.2}>{value}</Typography>
                        {helper && <Typography variant="caption" color="text.secondary">{helper}</Typography>}
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
}

// FilterCombo

function FilterCombo({ label, options, value, onChange }) {
    return (
        <Autocomplete
            multiple
            options={options}
            value={value}
            onChange={(_, val) => onChange(val)}
            size="small"
            fullWidth
            disableCloseOnSelect
            limitTags={2}
            sx={{
                "& .MuiAutocomplete-endAdornment": {
                    top: "50%",
                    transform: "translateY(-50%)",
                },
            }}
            renderOption={(props, option, { selected }) => (
                <li {...props}>
                    <Checkbox
                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                        style={{ marginRight: 6, padding: 0 }}
                        checked={selected}
                    />
                    {option}
                </li>
            )}
            renderInput={(params) => <TextField {...params} label={label} />}
            ListboxProps={{ style: { maxHeight: 220 } }}
        />
    );
}

// PreviewTable

function PreviewTable({ columns, rows, disableSticky = false }) {
    const minW = columns.reduce((s, c) => s + (c.width || 100), 0);

    return (
        <TableContainer sx={{ height: 460, overflowX: "auto", overflowY: "auto" }}>
            <Table
                stickyHeader
                size="small"
                sx={{
                    minWidth: minW,
                    "& .MuiTableCell-root": { borderRight: "1px solid #e0e0e0" },
                    "& .MuiTableCell-root:last-child": { borderRight: "none" },
                }}
            >
                <TableHead>
                    <TableRow>
                        {columns.map((col) => (
                            <TableCell
                                key={col.field}
                                align={col.align || "left"}
                                sx={{
                                    fontWeight: 700,
                                    bgcolor: (col.sticky && !disableSticky) || col.bold ? H_DARK : H_MID,
                                    color: "#fff",
                                    minWidth: col.width,
                                    ...(col.sticky && !disableSticky && {
                                        position: "sticky",
                                        left: col.left,
                                        zIndex: 5,
                                        boxShadow: col.stickyEnd ? "4px 0 6px -2px rgba(0,0,0,0.15)" : "none",
                                    }),
                                }}
                            >
                                {col.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {rows.map((row, idx) => {
                        const bg = idx % 2 === 0 ? R_EVEN : R_ODD;

                        return (
                            <TableRow key={row.id ?? idx} sx={{ bgcolor: bg, "&:hover td": { bgcolor: "#e8f5e9" } }}>
                                {columns.map((col) => {
                                    const raw = col.computed ? col.computed(row) : row[col.field];

                                    return (
                                        <TableCell
                                            key={col.field}
                                            align={col.align || "left"}
                                            sx={{
                                                minWidth: col.width,
                                                ...(col.sticky && !disableSticky && {
                                                    position: "sticky",
                                                    left: col.left,
                                                    bgcolor: bg,
                                                    zIndex: 2,
                                                    boxShadow: col.stickyEnd ? "4px 0 6px -2px rgba(0,0,0,0.08)" : "none",
                                                }),
                                                ...(col.bold && { fontWeight: 800, color: H_DARK, fontSize: "0.82rem" }),
                                            }}
                                        >
                                            {col.percent
                                                ? (raw == null || raw === ""
                                                    ? "-"
                                                    : `%${(Number(raw) * 100).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)
                                                : col.numeric || col.computed
                                                    ? (raw != null && raw !== "" ? Number(raw).toLocaleString("tr-TR") : "-")
                                                    : (raw || <Chip size="small" color="warning" label="Eksik" />)}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        );
                    })}

                    {rows.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={columns.length} align="center" sx={{ py: 8 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Kayıt bulunamadı. Yukarıdaki filtreleri kontrol edin veya yeni bir Excel dosyası yükleyin.
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

// UploadTabPanel

function UploadTabPanel({ rows, setRows, fileName, setFileName, columns, columnMap, hasFullFilters, requiredFields, onSave, saving, readOnly = false, year, bulkApplyConfig, label }) {
    const fileInputRef = useRef(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [searchText, setSearchText] = useState("");
    const [fP3, setFP3] = useState([]);
    const [fP4, setFP4] = useState([]);
    const [fBrand, setFBrand] = useState([]);
    const [fBiSo, setFBiSo] = useState([]);
    const [fSup, setFSup] = useState([]);
    const [bulkVal, setBulkVal] = useState("");

    const optP3 = useMemo(() => [...new Set(rows.map((r) => r.productLevel3).filter(Boolean))].sort((a, b) => a.localeCompare(b, "tr")), [rows]);
    const optP4 = useMemo(() => [...new Set(rows.map((r) => r.productLevel4).filter(Boolean))].sort((a, b) => a.localeCompare(b, "tr")), [rows]);
    const optBrand = useMemo(() => [...new Set(rows.map((r) => r.brand).filter(Boolean))].sort((a, b) => a.localeCompare(b, "tr")), [rows]);
    const optBiSo = useMemo(() => [...new Set(rows.map((r) => r.builtInSolo).filter(Boolean))].sort((a, b) => a.localeCompare(b, "tr")), [rows]);
    const optSup = useMemo(() => [...new Set(rows.map((r) => r.supplyLocation).filter(Boolean))].sort((a, b) => a.localeCompare(b, "tr")), [rows]);

    // Yeni excel yüklendiğinde filtreleri sıfırla
    useEffect(() => {
        setFP3([]); setFP4([]); setFBrand([]); setFBiSo([]); setFSup([]);
        setSearchText(""); setPage(0);
    }, [rows]);

    const hasFilter = fP3.length || fP4.length || fBrand.length || fBiSo.length || fSup.length || searchText;

    const clearFilters = () => {
        setFP3([]);
        setFP4([]);
        setFBrand([]);
        setFBiSo([]);
        setFSup([]);
        setSearchText("");
        setPage(0);
    };

    const filteredRows = useMemo(() => {
        let r = rows;

        if (fP3.length) r = r.filter((x) => fP3.includes(x.productLevel3));
        if (fP4.length) r = r.filter((x) => fP4.includes(x.productLevel4));
        if (fBrand.length) r = r.filter((x) => fBrand.includes(x.brand));
        if (fBiSo.length) r = r.filter((x) => fBiSo.includes(x.builtInSolo));
        if (fSup.length) r = r.filter((x) => fSup.includes(x.supplyLocation));

        const q = searchText.trim().toLocaleLowerCase("tr-TR");
        if (q) r = r.filter((x) => Object.values(x).join(" ").toLocaleLowerCase("tr-TR").includes(q));

        return r;
    }, [rows, fP3, fP4, fBrand, fBiSo, fSup, searchText]);

    const pagedRows = useMemo(
        () => filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [filteredRows, page, rowsPerPage]
    );

    const missingCount = useMemo(
        () => rows.filter((r) => requiredFields.some((f) => r[f] === undefined || r[f] === null || r[f] === "")).length,
        [rows, requiredFields]
    );

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setFileName(file.name);
        e.target.value = "";

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const wb = XLSX.read(new Uint8Array(evt.target.result), { type: "array" });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const rawRows = XLSX.utils.sheet_to_json(ws, { defval: "" });

                const parsed = rawRows
                    .map((excelRow, i) => {
                        const row = { id: i + 1 };
                        const excelKeys = Object.keys(excelRow);
                        columnMap.forEach(({ headers, field, transform }) => {
                            const matchKey = excelKeys.find((k) =>
                                headers.some((h) => normH(k) === h)
                            );
                            if (matchKey !== undefined) {
                                const raw = excelRow[matchKey];
                                row[field] = transform ? transform(raw) : (raw === "" ? "" : String(raw));
                            }
                        });
                        return row;
                    })
                    .filter((r) => Object.keys(r).length > 1);

                setRows(parsed);
            } catch (err) {
                console.error("Excel parse hatası:", err);
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const handleExportTabExcel = () => {
        const data = filteredRows.map((row) => {
            const obj = {};
            columns.forEach((col) => {
                const raw = col.computed ? col.computed(row) : row[col.field];
                obj[col.label] = col.percent ? (raw == null || raw === "" ? "" : Number(raw)) : (raw ?? "");
            });
            return obj;
        });

        const sheet = XLSX.utils.json_to_sheet(data);
        const percentColIdx = columns.map((c, i) => (c.percent ? i : -1)).filter((i) => i !== -1);
        if (percentColIdx.length && sheet["!ref"]) {
            const range = XLSX.utils.decode_range(sheet["!ref"]);
            for (let R = 1; R <= range.e.r; R++) {
                percentColIdx.forEach((C) => {
                    const cell = sheet[XLSX.utils.encode_cell({ r: R, c: C })];
                    if (cell && typeof cell.v === "number") cell.z = "0.00%";
                });
            }
        }

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, sheet, (label || "Veri").slice(0, 31));
        XLSX.writeFile(wb, `${label || "Veri"} - ${year}.xlsx`);
    };

    return (
        <Box>
            {/* Upload Alanı */}
            {(!readOnly || rows.length > 0) && (
                <Box sx={{ p: 2.5, borderBottom: "1px solid", borderColor: "divider" }}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} flexWrap="wrap">
                        {!readOnly && (
                            <>
                                <input ref={fileInputRef} type="file" accept=".xlsx,.xls" hidden onChange={handleFileSelect} />
                                <Button
                                    startIcon={<UploadFileIcon />}
                                    variant="contained"
                                    color="secondary"
                                    onClick={() => fileInputRef.current?.click()}
                                    sx={{ fontWeight: 700 }}
                                >
                                    Excel Yükle
                                </Button>

                                {fileName
                                    ? <Chip label={fileName} size="small" color="info" variant="outlined" onDelete={() => { setFileName(""); setRows([]); }} />
                                    : <Typography variant="body2" color="text.secondary">Henüz dosya seçilmedi.</Typography>
                                }
                            </>
                        )}

                        <Box sx={{ flexGrow: 1 }} />

                        {rows.length > 0 && (
                            <Button
                                variant="outlined"
                                color="secondary"
                                startIcon={<FileDownloadOutlinedIcon />}
                                onClick={handleExportTabExcel}
                                sx={{ fontWeight: 700 }}
                            >
                                Excel'e Aktar
                            </Button>
                        )}

                        {!readOnly && (
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                onClick={onSave}
                                disabled={rows.length === 0 || saving}
                                sx={{ fontWeight: 700, minWidth: 170 }}
                            >
                                {saving ? "Kaydediliyor..." : `Kaydet (${rows.length} kayıt)`}
                            </Button>
                        )}
                    </Stack>
                </Box>
            )}

            {/* Özet Kartlar */}
            <Box sx={{ p: 2.5, borderBottom: "1px solid", borderColor: "divider" }}>
                <Grid container spacing={2}>
                    <Grid
                        size={{
                            xs: 6,
                            md: 3
                        }}>
                        <SummaryCard title="Yüklenen Satır" value={rows.length.toLocaleString("tr-TR")} helper="Excel kayıt sayısı" icon={<Inventory2OutlinedIcon />} />
                    </Grid>

                    <Grid
                        size={{
                            xs: 6,
                            md: 3
                        }}>
                        <SummaryCard title="Filtrelenmiş" value={filteredRows.length.toLocaleString("tr-TR")} helper="Görüntülenen" icon={<AssessmentOutlinedIcon />} color="info.main" />
                    </Grid>

                    <Grid
                        size={{
                            xs: 6,
                            md: 3
                        }}>
                        <SummaryCard title="Eksik Alan" value={missingCount.toLocaleString("tr-TR")} helper="Zorunlu alan eksik" icon={<ErrorOutlineIcon />} color={missingCount > 0 ? "warning.main" : "secondary.main"} />
                    </Grid>

                    <Grid
                        size={{
                            xs: 6,
                            md: 3
                        }}>
                        <SummaryCard title="Plan Yılı" value={year} helper="Kaydedilecek yıl" icon={<CalendarMonthOutlinedIcon />} color="primary.main" />
                    </Grid>
                </Grid>

                {missingCount > 0 && (
                    <MuiAlert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                        {missingCount} kayıtta zorunlu alanlar eksik. Excel dosyasını kontrol edin.
                    </MuiAlert>
                )}
            </Box>

            {/* Filtreler */}
            <Box sx={{ p: 2.5, bgcolor: "#f8faf8", borderBottom: "1px solid", borderColor: "divider" }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <FilterListIcon fontSize="small" sx={{ color: H_DARK }} />
                    <Typography variant="subtitle2" fontWeight={700} color={H_DARK}>Filtreler</Typography>

                    {hasFilter && (
                        <Chip
                            label="Temizle"
                            size="small"
                            color="error"
                            variant="outlined"
                            onDelete={clearFilters}
                            onClick={clearFilters}
                            sx={{ ml: 1 }}
                        />
                    )}
                </Stack>

                <Grid container spacing={1.5}>
                    <Grid
                        size={{
                            xs: 12,
                            sm: 6,
                            md: hasFullFilters ? 2 : 4
                        }}>
                        <FilterCombo
                            label="Ürün (3. Seviye)"
                            options={optP3}
                            value={fP3}
                            onChange={(v) => { setFP3(v); setPage(0); }}
                        />
                    </Grid>

                    <Grid
                        size={{
                            xs: 12,
                            sm: 6,
                            md: hasFullFilters ? 2.5 : 4
                        }}>
                        <FilterCombo
                            label="Ürün (4. Seviye)"
                            options={optP4}
                            value={fP4}
                            onChange={(v) => { setFP4(v); setPage(0); }}
                        />
                    </Grid>

                    {hasFullFilters && (
                        <>
                            <Grid
                                size={{
                                    xs: 12,
                                    sm: 6,
                                    md: 2.5
                                }}>
                                <FilterCombo
                                    label="Marka"
                                    options={optBrand}
                                    value={fBrand}
                                    onChange={(v) => { setFBrand(v); setPage(0); }}
                                />
                            </Grid>

                            <Grid
                                size={{
                                    xs: 12,
                                    sm: 6,
                                    md: 1.5
                                }}>
                                <FilterCombo
                                    label="Ankastre/Solo"
                                    options={optBiSo}
                                    value={fBiSo}
                                    onChange={(v) => { setFBiSo(v); setPage(0); }}
                                />
                            </Grid>

                            <Grid
                                size={{
                                    xs: 12,
                                    sm: 6,
                                    md: 1.5
                                }}>
                                <FilterCombo
                                    label="Temin Yeri"
                                    options={optSup}
                                    value={fSup}
                                    onChange={(v) => { setFSup(v); setPage(0); }}
                                />
                            </Grid>
                        </>
                    )}

                    <Grid
                        size={{
                            xs: 12,
                            sm: 6,
                            md: hasFullFilters ? 2 : 4
                        }}>
                        <TextField
                            size="small"
                            fullWidth
                            value={searchText}
                            onChange={(e) => { setSearchText(e.target.value); setPage(0); }}
                            placeholder="Serbest ara..."
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* Hepsine Uygula (toplu doldurma) */}
            {bulkApplyConfig && !readOnly && (
                <Box sx={{ p: 1.5, display: "flex", gap: 1.5, alignItems: "center", bgcolor: "#fff8e1", borderBottom: "1px solid", borderColor: "divider", flexWrap: "wrap" }}>
                    <TextField
                        size="small"
                        label={bulkApplyConfig.label}
                        value={bulkVal}
                        onChange={(e) => setBulkVal(e.target.value)}
                        sx={{ width: 220 }}
                    />
                    <Button
                        variant="outlined"
                        color="secondary"
                        disabled={bulkVal === "" || filteredRows.length === 0}
                        onClick={() => {
                            const val = toNum(bulkVal);
                            const ids = new Set(filteredRows.map((r) => r.id));
                            setRows(rows.map((r) => (ids.has(r.id)
                                ? { ...r, ...Object.fromEntries(bulkApplyConfig.fields.map((f) => [f, val])) }
                                : r)));
                        }}
                    >
                        Filtrelenen Satırlara Uygula ({filteredRows.length})
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                        Yukarıdaki filtreleri kullanarak satır seçip tek değeri toplu uygulayabilirsiniz.
                    </Typography>
                </Box>
            )}

            {/* Tablo */}
            <Divider />

            <PreviewTable columns={columns} rows={pagedRows} />

            {/* Pagination */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    bgcolor: "#fafafa",
                    flexWrap: "wrap",
                    gap: 1,
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="caption" color="text.secondary">Sayfa başı satır:</Typography>
                    {[50, 100, 200, 500, 1000].map((opt) => (
                        <Box
                            key={opt}
                            onClick={() => { setRowsPerPage(opt); setPage(0); }}
                            sx={{
                                px: 1.5, py: 0.4,
                                borderRadius: 1,
                                cursor: "pointer",
                                fontSize: "0.8rem",
                                fontWeight: rowsPerPage === opt ? 700 : 400,
                                bgcolor: rowsPerPage === opt ? H_DARK : "transparent",
                                color: rowsPerPage === opt ? "#fff" : "text.secondary",
                                border: "1px solid",
                                borderColor: rowsPerPage === opt ? H_DARK : "divider",
                                "&:hover": { bgcolor: rowsPerPage === opt ? H_DARK : "grey.100" },
                            }}
                        >
                            {opt}
                        </Box>
                    ))}
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="caption" color="text.secondary">
                        {filteredRows.length === 0
                            ? "0 kayıt"
                            : `${page * rowsPerPage + 1}–${Math.min((page + 1) * rowsPerPage, filteredRows.length)} / ${filteredRows.length} kayıt`}
                    </Typography>
                    <Box
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                        sx={{
                            width: 28, height: 28, display: "grid", placeItems: "center",
                            borderRadius: 1, border: "1px solid", borderColor: "divider",
                            cursor: page === 0 ? "default" : "pointer",
                            color: page === 0 ? "text.disabled" : "text.primary",
                            "&:hover": { bgcolor: page === 0 ? "transparent" : "grey.100" },
                        }}
                    >
                        ‹
                    </Box>
                    {Array.from({ length: Math.ceil(filteredRows.length / rowsPerPage) }, (_, i) => i)
                        .filter((i) => {
                            const total = Math.ceil(filteredRows.length / rowsPerPage);
                            return i === 0 || i === total - 1 || Math.abs(i - page) <= 1;
                        })
                        .reduce((acc, i, idx, arr) => {
                            if (idx > 0 && i - arr[idx - 1] > 1) acc.push("...");
                            acc.push(i);
                            return acc;
                        }, [])
                        .map((item, idx) =>
                            item === "..." ? (
                                <Typography
                                    key={"e" + idx}
                                    variant="caption"
                                    color="text.disabled" sx={{ px: 0.5 }}
                                >
                                    …
                                </Typography>
                            ) : (
                                <Box
                                    key={item}
                                    onClick={() => setPage(item)}
                                    sx={{
                                        width: 28, height: 28, display: "grid", placeItems: "center",
                                        borderRadius: 1, border: "1px solid",
                                        borderColor: page === item ? H_DARK : "divider",
                                        bgcolor: page === item ? H_DARK : "transparent",
                                        color: page === item ? "#fff" : "text.primary",
                                        cursor: "pointer",
                                        fontSize: "0.8rem",
                                        fontWeight: page === item ? 700 : 400,
                                        "&:hover": { bgcolor: page === item ? H_DARK : "grey.100" },
                                    }}
                                >
                                    {item + 1}
                                </Box>
                            )
                        )}
                    <Box
                        onClick={() => setPage((p) => Math.min(Math.ceil(filteredRows.length / rowsPerPage) - 1, p + 1))}
                        sx={{
                            width: 28, height: 28, display: "grid", placeItems: "center",
                            borderRadius: 1, border: "1px solid", borderColor: "divider",
                            cursor: page >= Math.ceil(filteredRows.length / rowsPerPage) - 1 ? "default" : "pointer",
                            color: page >= Math.ceil(filteredRows.length / rowsPerPage) - 1 ? "text.disabled" : "text.primary",
                            "&:hover": { bgcolor: page >= Math.ceil(filteredRows.length / rowsPerPage) - 1 ? "transparent" : "grey.100" },
                        }}
                    >
                        ›
                    </Box>
                </Stack>
            </Box>
        </Box>
    );
}

// Ana Sayfa
export default function ConstructionBudgetEntryPage() {
    const [selectedYear, setSelectedYear] = useState(2026);
    const [activeTab, setActiveTab] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [queryLoading, setQueryLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [calcLoading, setCalcLoading] = useState(false);
    const [calcConfirmOpen, setCalcConfirmOpen] = useState(false);
    const [resultModalOpen, setResultModalOpen] = useState(false);

    const showSnackbar = (message, severity = "success") =>
        setSnackbar({ open: true, message, severity });

    // Tab 0 - Satış Adetleri
    const [salesRows, setSalesRows] = useState([]);
    const [salesFileName, setSalesFileName] = useState("");

    // Tab 1 - Gerçekleşen Adetler
    const [actualRows, setActualRows] = useState([]);
    const [actualFileName, setActualFileName] = useState("");

    // Tab 2 - Montaj Oranı
    const [arRows, setArRows] = useState([]);
    const [arFileName, setArFileName] = useState("");

    // Tab 3 - Mevsimsel İndeks
    const [seaRows, setSeaRows] = useState([]);
    const [seaFileName, setSeaFileName] = useState("");

    // Tab 4 - Hesaplanan Montaj Adetleri (salt okunur)
    const [calcRows, setCalcRows] = useState([]);
    const [calcFileName, setCalcFileName] = useState("");

    // Tab 5 - SAP-BPC Oranı (salt okunur, hesaplanmış)
    const [sapBpcRows, setSapBpcRows] = useState([]);
    const [sapBpcFileName, setSapBpcFileName] = useState("");

    // Tab 6 - Montaj Birim Maliyeti Girdi
    const [unitCostRows, setUnitCostRows] = useState([]);
    const [unitCostFileName, setUnitCostFileName] = useState("");

    // Tab 7 - Montaj Birim Maliyeti (Hesaplanan, salt okunur)
    const [unitCostCalcRows, setUnitCostCalcRows] = useState([]);
    const [unitCostCalcFileName, setUnitCostCalcFileName] = useState("");

    // Save handlers

    const handleSalesQtySave = () => {
        const payload = salesRows.map((r) => ({
            productLevelThree: r.productLevel3,
            productLevelFour: r.productLevel4,
            brand: r.brand,
            builtInOrStandalone: r.builtInSolo,
            placeOfSupply: r.supplyLocation,
            january: r.jan,
            february: r.feb,
            march: r.mar,
            april: r.apr,
            may: r.may,
            june: r.jun,
            july: r.jul,
            august: r.aug,
            september: r.sep,
            october: r.oct,
            november: r.nov,
            december: r.dec,
            year: selectedYear,
        }));

        console.log("InstallationBudgetSalesQuantityInsert ->", payload);
        setSaving(true);
        axios.post("/InstallationBudgetSalesQuantityInsert", payload)
            .then(() => {
                showSnackbar("Satış adetleri başarıyla kaydedildi.");
                setSalesRows([]);
                setSalesFileName("");
            })
            .catch((err) => {
                console.error("Satış adetleri kaydetme hatası:", err);
                showSnackbar("Satış adetleri kaydedilirken bir hata oluştu. Lütfen tekrar deneyin.", "error");
            })
            .finally(() => setSaving(false));
    };

    const handleActualQtySave = () => {
        const payload = actualRows.map((r) => ({
            productLevelThree: r.productLevel3,
            productLevelFour: r.productLevel4,
            brand: r.brand,
            builtInOrStandalone: r.builtInSolo,
            placeOfSupply: r.supplyLocation,
            totalQuantity: r.totalQuantity,
            monthCount: r.monthCount,
            year: selectedYear,
        }));

        console.log("InstallationBudgetActualQuantityInsert ->", payload);
        setSaving(true);
        axios.post("/InstallationBudgetActualQuantityInsert", payload)
            .then(() => {
                showSnackbar("Gerçekleşen adetler başarıyla kaydedildi.");
                setActualRows([]);
                setActualFileName("");
            })
            .catch((err) => {
                console.error("Gerçekleşen adetler kaydetme hatası:", err);
                showSnackbar("Gerçekleşen adetler kaydedilirken bir hata oluştu. Lütfen tekrar deneyin.", "error");
            })
            .finally(() => setSaving(false));
    };

    const handleAssemblyRateSave = () => {
        const payload = arRows.map((r) => ({
            productLevelThree: r.productLevel3,
            productLevelFour: r.productLevel4,
            assemblyRate: r.assemblyRate,
            year: selectedYear,
        }));

        console.log("InstallationBudgetAssemblyRateInsert ->", payload);
        setSaving(true);
        axios.post("/InstallationBudgetAssemblyRateInsert", payload)
            .then(() => {
                showSnackbar("Montaj oranları başarıyla kaydedildi.");
                setArRows([]);
                setArFileName("");
            })
            .catch((err) => {
                console.error("Montaj oranları kaydetme hatası:", err);
                showSnackbar("Montaj oranları kaydedilirken bir hata oluştu. Lütfen tekrar deneyin.", "error");
            })
            .finally(() => setSaving(false));
    };

    const handleSeasonalSave = () => {
        const payload = seaRows.map((r) => ({
            productLevelThree: r.productLevel3,
            productLevelFour: r.productLevel4,
            brand: r.brand,
            builtInOrStandalone: r.builtInSolo,
            placeOfSupply: r.supplyLocation,
            januaryAssembly: r.janIdx,
            februaryAssembly: r.febIdx,
            marchAssembly: r.marIdx,
            aprilAssembly: r.aprIdx,
            mayAssembly: r.mayIdx,
            juneAssembly: r.junIdx,
            julyAssembly: r.julIdx,
            augustAssembly: r.augIdx,
            septemberAssembly: r.sepIdx,
            octoberAssembly: r.octIdx,
            novemberAssembly: r.novIdx,
            decemberAssembly: r.decIdx,
            year: selectedYear,
        }));

        console.log("InstallationBudgetSeasonalIndexInsert ->", payload);
        setSaving(true);
        axios.post("/InstallationBudgetSeasonalIndexInsert", payload)
            .then(() => {
                showSnackbar("Mevsimsel indeksler başarıyla kaydedildi.");
                setSeaRows([]);
                setSeaFileName("");
            })
            .catch((err) => {
                console.error("Mevsimsel indeksler kaydetme hatası:", err);
                showSnackbar("Mevsimsel indeksler kaydedilirken bir hata oluştu. Lütfen tekrar deneyin.", "error");
            })
            .finally(() => setSaving(false));
    };

    const handleUnitCostSave = () => {
        const payload = unitCostRows.map((r) => {
            const item = {
                productLevelThree: r.productLevel3,
                productLevelFour: r.productLevel4,
                builtInOrStandalone: r.builtInSolo,
                laborBaseAmount: r.laborBase,
                amount554BaseAmount: r.amt554Base,
                surveyAmount: r.surveyAmount,
                year: selectedYear,
            };
            MONTHS.forEach((m, idx) => {
                const p = MONTH_PASCAL[idx];
                item[`labor${p}Rate`] = r[`labor${capField(m.field)}Rate`];
                item[`amount554${p}Rate`] = r[`amt554${capField(m.field)}Rate`];
                item[`material${p}`] = r[`mat${capField(m.field)}`];
            });
            return item;
        });

        console.log("InstallationBudgetUnitCostInsert ->", payload);
        setSaving(true);
        axios.post("/InstallationBudgetUnitCostInsert", payload)
            .then(() => {
                showSnackbar("Montaj birim maliyeti girdileri başarıyla kaydedildi.");
                setUnitCostRows([]);
                setUnitCostFileName("");
            })
            .catch((err) => {
                console.error("Montaj birim maliyeti kaydetme hatası:", err);
                showSnackbar("Montaj birim maliyeti kaydedilirken bir hata oluştu. Lütfen tekrar deneyin.", "error");
            })
            .finally(() => setSaving(false));
    };

    const TAB_PANELS = [
        {
            rows: salesRows,
            setRows: setSalesRows,
            fileName: salesFileName,
            setFileName: setSalesFileName,
            label: "Satış Adetleri",
            columns: SALES_QTY_COLS,
            columnMap: SALES_COL_MAP,
            hasFullFilters: true,
            requiredFields: ["productLevel3", "productLevel4", "brand", "builtInSolo", "supplyLocation"],
            onSave: handleSalesQtySave,
            saving,
        },
        {
            rows: actualRows,
            setRows: setActualRows,
            fileName: actualFileName,
            setFileName: setActualFileName,
            label: "Gerçekleşen Adetler",
            columns: ACTUAL_QTY_COLS,
            columnMap: ACTUAL_COL_MAP,
            hasFullFilters: true,
            requiredFields: ["productLevel3", "productLevel4", "brand", "builtInSolo", "supplyLocation", "totalQuantity"],
            onSave: handleActualQtySave,
            saving,
        },
        {
            rows: arRows,
            setRows: setArRows,
            fileName: arFileName,
            setFileName: setArFileName,
            label: "Montaj Oranı",
            columns: ASSEMBLY_RATE_COLS,
            columnMap: ASSEMBLY_COL_MAP,
            hasFullFilters: false,
            requiredFields: ["productLevel3", "productLevel4", "assemblyRate"],
            onSave: handleAssemblyRateSave,
            saving,
        },
        {
            rows: seaRows,
            setRows: setSeaRows,
            fileName: seaFileName,
            setFileName: setSeaFileName,
            label: "Mevsimsel İndeks",
            columns: SEASONAL_IDX_COLS,
            columnMap: SEASONAL_COL_MAP,
            hasFullFilters: true,
            requiredFields: ["productLevel3", "productLevel4", "brand", "builtInSolo", "supplyLocation"],
            onSave: handleSeasonalSave,
            saving,
        },
        {
            rows: calcRows,
            setRows: setCalcRows,
            fileName: calcFileName,
            setFileName: setCalcFileName,
            label: "Hesaplanan Montaj Adetleri",
            columns: CALC_ASSEMBLY_COLS,
            columnMap: [],
            hasFullFilters: true,
            requiredFields: [],
            onSave: null,
            saving: false,
            readOnly: true,
        },
        {
            rows: sapBpcRows,
            setRows: setSapBpcRows,
            fileName: sapBpcFileName,
            setFileName: setSapBpcFileName,
            label: "SAP-BPC Oranı",
            columns: SAP_BPC_RATE_COLS,
            columnMap: [],
            hasFullFilters: true,
            requiredFields: [],
            onSave: null,
            saving: false,
            readOnly: true,
        },
        {
            rows: unitCostRows,
            setRows: setUnitCostRows,
            fileName: unitCostFileName,
            setFileName: setUnitCostFileName,
            label: "Montaj Birim Maliyeti Girdi",
            columns: UNIT_COST_INPUT_COLS,
            columnMap: UNIT_COST_COL_MAP,
            hasFullFilters: false,
            requiredFields: ["productLevel3", "productLevel4", "builtInSolo"],
            onSave: handleUnitCostSave,
            saving,
            // "Hepsine Uygula" (Malzeme Tutarı) şimdilik kapalı - istenirse aşağıdaki satır geri açılabilir.
            // bulkApplyConfig: { label: "Malzeme Tutarı (Tüm Aylar)", fields: MONTHS.map((m) => `mat${capField(m.field)}`) },
        },
        {
            rows: unitCostCalcRows,
            setRows: setUnitCostCalcRows,
            fileName: unitCostCalcFileName,
            setFileName: setUnitCostCalcFileName,
            label: "Montaj Birim Maliyeti (Hesaplanan)",
            columns: UNIT_COST_CALC_COLS,
            columnMap: [],
            hasFullFilters: true,
            requiredFields: [],
            onSave: null,
            saving: false,
            readOnly: true,
        },
    ];

    const handleSnackbarClose = (_, reason) => {
        if (reason === "clickaway") return;
        setSnackbar((s) => ({ ...s, open: false }));
    };

    const QueryMap = [
        {
            endpoint: "GetInstallationBudgetSalesQuantity",
            setRows: setSalesRows,
            setFileName: setSalesFileName,
            map: (r, i) => ({
                id: i + 1,
                productLevel3: r.ProductLevelThree,
                productLevel4: r.ProductLevelFour,
                brand: r.Brand,
                builtInSolo: r.BuiltInOrStandalone,
                supplyLocation: r.PlaceOfSupply,
                jan: r.January ?? 0, feb: r.February ?? 0, mar: r.March ?? 0,
                apr: r.April ?? 0, may: r.May ?? 0, jun: r.June ?? 0,
                jul: r.July ?? 0, aug: r.August ?? 0, sep: r.September ?? 0,
                oct: r.October ?? 0, nov: r.November ?? 0, dec: r.December ?? 0,
            }),
        },
        {
            endpoint: "GetInstallationBudgetActualQuantity",
            setRows: setActualRows,
            setFileName: setActualFileName,
            map: (r, i) => ({
                id: i + 1,
                productLevel3: r.ProductLevelThree,
                productLevel4: r.ProductLevelFour,
                brand: r.Brand,
                builtInSolo: r.BuiltInOrStandalone,
                supplyLocation: r.PlaceOfSupply,
                totalQuantity: r.TotalQuantity ?? 0,
                monthCount: r.MonthCount ?? 0,
            }),
        },
        {
            endpoint: "GetInstallationBudgetAssemblyRate",
            setRows: setArRows,
            setFileName: setArFileName,
            map: (r, i) => ({
                id: i + 1,
                productLevel3: r.ProductLevelThree,
                productLevel4: r.ProductLevelFour,
                assemblyRate: r.AssemblyRate != null ? r.AssemblyRate * 100 : 0,
            }),
        },
        {
            endpoint: "GetInstallationBudgetSeasonalIndex",
            setRows: setSeaRows,
            setFileName: setSeaFileName,
            map: (r, i) => ({
                id: i + 1,
                productLevel3: r.ProductLevelThree,
                productLevel4: r.ProductLevelFour,
                brand: r.Brand,
                builtInSolo: r.BuiltInOrStandalone,
                supplyLocation: r.PlaceOfSupply,
                janIdx: r.JanuaryAssembly ?? 0, febIdx: r.FebruaryAssembly ?? 0,
                marIdx: r.MarchAssembly ?? 0, aprIdx: r.AprilAssembly ?? 0,
                mayIdx: r.MayAssembly ?? 0, junIdx: r.JuneAssembly ?? 0,
                julIdx: r.JulyAssembly ?? 0, augIdx: r.AugustAssembly ?? 0,
                sepIdx: r.SeptemberAssembly ?? 0, octIdx: r.OctoberAssembly ?? 0,
                novIdx: r.NovemberAssembly ?? 0, decIdx: r.DecemberAssembly ?? 0,
            }),
        },
        {
            endpoint: "GetInstallationBudgetCalculatedAssemblyQuantity",
            setRows: setCalcRows,
            setFileName: setCalcFileName,
            map: (r, i) => ({
                id: i + 1,
                productLevel3: r.ProductLevelThree,
                productLevel4: r.ProductLevelFour,
                brand: r.Brand,
                builtInSolo: r.BuiltInOrStandalone,
                supplyLocation: r.PlaceOfSupply,
                jan: r.January ?? 0, feb: r.February ?? 0, mar: r.March ?? 0,
                apr: r.April ?? 0, may: r.May ?? 0, jun: r.June ?? 0,
                jul: r.July ?? 0, aug: r.August ?? 0, sep: r.September ?? 0,
                oct: r.October ?? 0, nov: r.November ?? 0, dec: r.December ?? 0,
            }),
        },
        {
            endpoint: "GetInstallationBudgetSapBpcRatio",
            setRows: setSapBpcRows,
            setFileName: setSapBpcFileName,
            map: (r, i) => ({
                id: i + 1,
                productLevel3: r.ProductLevelThree,
                productLevel4: r.ProductLevelFour,
                brand: r.Brand,
                builtInSolo: r.BuiltInOrStandalone,
                supplyLocation: r.PlaceOfSupply,
                jan: r.January ?? null, feb: r.February ?? null, mar: r.March ?? null,
                apr: r.April ?? null, may: r.May ?? null, jun: r.June ?? null,
                jul: r.July ?? null, aug: r.August ?? null, sep: r.September ?? null,
                oct: r.October ?? null, nov: r.November ?? null, dec: r.December ?? null,
            }),
        },
        {
            endpoint: "GetInstallationBudgetUnitCost",
            setRows: setUnitCostRows,
            setFileName: setUnitCostFileName,
            map: (r, i) => {
                const row = {
                    id: i + 1,
                    productLevel3: r.ProductLevelThree,
                    productLevel4: r.ProductLevelFour,
                    builtInSolo: r.BuiltInOrStandalone,
                    laborBase: r.LaborBaseAmount ?? 0,
                    amt554Base: r.Amount554BaseAmount ?? 0,
                    surveyAmount: r.SurveyAmount ?? 0,
                };
                MONTHS.forEach((m, idx) => {
                    const p = MONTH_PASCAL[idx];
                    row[`labor${capField(m.field)}Rate`] = r[`Labor${p}Rate`] != null ? r[`Labor${p}Rate`] * 100 : 0;
                    row[`amt554${capField(m.field)}Rate`] = r[`Amount554${p}Rate`] != null ? r[`Amount554${p}Rate`] * 100 : 0;
                    row[`mat${capField(m.field)}`] = r[`Material${p}`] ?? 0;
                });
                return row;
            },
        },
        {
            endpoint: "GetInstallationBudgetUnitCostCalculated",
            setRows: setUnitCostCalcRows,
            setFileName: setUnitCostCalcFileName,
            map: (r, i) => ({
                id: i + 1,
                productLevel3: r.ProductLevelThree,
                productLevel4: r.ProductLevelFour,
                brand: r.Brand,
                builtInSolo: r.BuiltInOrStandalone,
                supplyLocation: r.PlaceOfSupply,
                jan: r.January ?? 0, feb: r.February ?? 0, mar: r.March ?? 0,
                apr: r.April ?? 0, may: r.May ?? 0, jun: r.June ?? 0,
                jul: r.July ?? 0, aug: r.August ?? 0, sep: r.September ?? 0,
                oct: r.October ?? 0, nov: r.November ?? 0, dec: r.December ?? 0,
                gider_jan: r.JanuaryExpense ?? 0, gider_feb: r.FebruaryExpense ?? 0, gider_mar: r.MarchExpense ?? 0,
                gider_apr: r.AprilExpense ?? 0, gider_may: r.MayExpense ?? 0, gider_jun: r.JuneExpense ?? 0,
                gider_jul: r.JulyExpense ?? 0, gider_aug: r.AugustExpense ?? 0, gider_sep: r.SeptemberExpense ?? 0,
                gider_oct: r.OctoberExpense ?? 0, gider_nov: r.NovemberExpense ?? 0, gider_dec: r.DecemberExpense ?? 0,
            }),
        },
    ];

    // Tek bir tab'ın verisini sunucudan çekip ilgili state'e yazar. "Sorgula" ve "Hesapla" sonrası
    // yenileme AYNI bu fonksiyonu kullanır - hiçbiri client-side hesaplama yapmaz, sadece DB'yi okur.
    const fetchTab = (idx) => {
        const cfg = QueryMap[idx];
        return axios.get(cfg.endpoint, { params: { Year: selectedYear } })
            .then((res) => {
                const rows = Array.isArray(res.data) ? res.data : [];
                cfg.setRows(rows.map(cfg.map));
                cfg.setFileName(`${selectedYear} yılı sorgu sonucu`);
            });
    };

    const handleCalculate = () => {
        setCalcConfirmOpen(false);
        setCalcLoading(true);
        const data = {
            Year: selectedYear
        }
        axios.post("/ExecuteInstallationCountBudgetCalculation", data)
            .then((res) => {
                const d = res.data;
                if (d?.ErrorCode === "0") {
                    // Backend'de sırayla hesaplanıp kaydedilen 3 sonucu (montaj adedi, SAP-BPC oranı,
                    // montaj birim maliyeti) tek "Hesapla" tıklamasıyla sorgulayıp ekrana getirir.
                    setActiveTab(4);
                    return Promise.all([fetchTab(4), fetchTab(5), fetchTab(7)])
                        .then(() => {
                            setResultModalOpen(true);
                        })
                        .catch(() => showSnackbar("Hesaplanan sonuçlar sorgulanırken hata oluştu.", "error"));
                } else {
                    showSnackbar(d?.ErrorDescription || "Hesaplama sırasında bir hata oluştu.", "error");
                }
            })
            .catch(() => showSnackbar("Hesaplama sırasında bir hata oluştu. Lütfen tekrar deneyin.", "error"))
            .finally(() => setCalcLoading(false));
    };

    const handleExportResultsToExcel = () => {
        const dimHeaders = ["Ürün (3. Seviye)", "Ürün (4. Seviye)", "Marka", "Ankastre/Solo", "Temin Yeri"];

        const sapBpcData = sapBpcRows.map((r) => {
            const row = {};
            dimHeaders.forEach((h, idx) => {
                row[h] = [r.productLevel3, r.productLevel4, r.brand, r.builtInSolo, r.supplyLocation][idx];
            });
            MONTHS.forEach((m) => {
                // Ham kesir değeri (0.2514) bırakılır; hücre formatı aşağıda "0.00%" yapılarak
                // Excel'in kendisi %25,14 olarak gösterir (gerçek yüzde hücresi, düz metin değil).
                row[m.label] = r[m.field] == null ? "" : Number(r[m.field]);
            });
            return row;
        });

        const unitCostData = unitCostCalcRows.map((r) => {
            const row = {};
            dimHeaders.forEach((h, idx) => {
                row[h] = [r.productLevel3, r.productLevel4, r.brand, r.builtInSolo, r.supplyLocation][idx];
            });
            MONTHS.forEach((m) => {
                row[m.label] = r[m.field] ?? "";
            });
            return row;
        });

        const wb = XLSX.utils.book_new();
        const sapBpcSheet = XLSX.utils.json_to_sheet(sapBpcData);

        // Ay kolonlarını (boyut kolonlarından sonraki tüm kolonlar) yüzde formatına çevir.
        const range = XLSX.utils.decode_range(sapBpcSheet["!ref"]);
        for (let R = 1; R <= range.e.r; R++) {
            for (let C = dimHeaders.length; C <= range.e.c; C++) {
                const cell = sapBpcSheet[XLSX.utils.encode_cell({ r: R, c: C })];
                if (cell && typeof cell.v === "number") {
                    cell.z = "0.00%";
                }
            }
        }

        XLSX.utils.book_append_sheet(wb, sapBpcSheet, "SAP-BPC Oranları");
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(unitCostData), "Montaj Birim Maliyeti");
        XLSX.writeFile(wb, `Hesaplama_Sonuclari_${selectedYear}.xlsx`);
    };

    const handleQuery = () => {
        setQueryLoading(true);
        fetchTab(activeTab)
            .catch(() => showSnackbar("Veri sorgulanırken hata oluştu. Lütfen tekrar deneyin.", "error"))
            .finally(() => setQueryLoading(false));
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#f0f4f8", minHeight: "100vh" }}>
            <Stack spacing={3}>
                {/* Başlık */}
                <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: "1px solid", borderColor: "divider", bgcolor: "#fff" }}>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h5" fontWeight={800} color={H_DARK}>
                                Müşteri Hizmetleri Bütçe Hesap Yönetimi
                            </Typography>

                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                Girdi dosyalarını yükleyip önizleyebilir, hesaplanan sonuçları (montaj adedi, SAP-BPC oranı, birim maliyet) görüntüleyebilirsiniz.
                            </Typography>
                        </Box>

                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                            <Autocomplete
                                options={YEARS}
                                value={selectedYear}
                                onChange={(_, v) => v && setSelectedYear(v)}
                                getOptionLabel={(opt) => String(opt)}
                                disableClearable
                                size="small"
                                sx={{
                                    width: { xs: "100%", md: 140 },
                                    "& .MuiAutocomplete-endAdornment": {
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                    },
                                }}
                                renderInput={(params) => <TextField {...params} label="Plan Yılı" />}
                            />
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={queryLoading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                                onClick={handleQuery}
                                disabled={queryLoading || calcLoading}
                                sx={{ fontWeight: 700, whiteSpace: "nowrap", height: 40 }}
                            >
                                {queryLoading ? "Sorgulanıyor..." : "Sorgula"}
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={calcLoading ? <CircularProgress size={16} color="inherit" /> : <PlayCircleOutlineIcon />}
                                onClick={() => setCalcConfirmOpen(true)}
                                disabled={calcLoading || queryLoading}
                                style={{ backgroundColor: calcLoading || queryLoading ? undefined : "#00695c", color: "#fff" }}
                                sx={{ fontWeight: 700, whiteSpace: "nowrap", height: 40 }}
                            >
                                {calcLoading ? "Hesaplanıyor..." : "Hesapla"}
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>

                {/* Tab Kartı */}
                <Paper elevation={0} sx={{ borderRadius: 4, border: "1px solid", borderColor: "divider", overflow: "hidden", bgcolor: "#fff" }}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", borderBottom: "2px solid", borderColor: "divider", bgcolor: "#fff" }}>
                        {[
                            { label: "Satış Adetleri", icon: <AssessmentOutlinedIcon />, color: "#2e7d32", bg: "#e8f5e9" },
                            { label: "Gerçekleşen Adetler", icon: <Inventory2OutlinedIcon />, color: "#1565c0", bg: "#e3f2fd" },
                            { label: "Montaj Oranı", icon: <SettingsOutlinedIcon />, color: "#e65100", bg: "#fff3e0" },
                            { label: "Mevsimsel İndeks", icon: <TrendingUpIcon />, color: "#6a1b9a", bg: "#f3e5f5" },
                            { label: "Hesaplanan Montaj Adetleri", icon: <CalculateOutlinedIcon />, color: "#00695c", bg: "#e0f2f1" },
                            { label: "SAP-BPC Oranı", icon: <PercentIcon />, color: "#c62828", bg: "#ffebee" },
                            { label: "Montaj Birim Maliyeti Girdi", icon: <PaidOutlinedIcon />, color: "#4527a0", bg: "#ede7f6" },
                            { label: "Montaj Birim Maliyeti (Hesaplanan)", icon: <ReceiptLongOutlinedIcon />, color: "#00838f", bg: "#e0f7fa" },
                        ].map((tab, i) => {
                            const active = activeTab === i;
                            return (
                                <Box
                                    key={i}
                                    onClick={() => setActiveTab(i)}
                                    sx={{
                                        flex: "1 1 160px",
                                        minWidth: 160,
                                        maxWidth: 260,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 1,
                                        px: 2,
                                        py: 1.75,
                                        cursor: "pointer",
                                        userSelect: "none",
                                        borderBottom: active ? `3px solid ${tab.color}` : "3px solid transparent",
                                        bgcolor: active ? tab.bg : "transparent",
                                        color: active ? tab.color : "text.secondary",
                                        fontWeight: active ? 700 : 400,
                                        fontSize: "0.875rem",
                                        borderRight: "1px solid",
                                        borderRightColor: "divider",
                                        transition: "all 0.15s ease",
                                        "&:hover": {
                                            bgcolor: active ? tab.bg : "grey.50",
                                            color: tab.color,
                                        },
                                    }}
                                >
                                    <Box sx={{
                                        width: 32, height: 32, borderRadius: 1.5,
                                        display: "grid", placeItems: "center",
                                        bgcolor: active ? tab.color : "grey.200",
                                        color: active ? "#fff" : "text.secondary",
                                        flexShrink: 0,
                                        "& svg": { fontSize: 18 },
                                    }}>
                                        {tab.icon}
                                    </Box>
                                    <Typography variant="body2" fontWeight="inherit" color="inherit" noWrap>
                                        {tab.label}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>

                    {TAB_PANELS.map((props, i) => (
                        <Box key={i} sx={{ display: activeTab === i ? "block" : "none" }}>
                            <UploadTabPanel {...props} year={selectedYear} />
                        </Box>
                    ))}
                </Paper>
            </Stack>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={10000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    variant={snackbar.severity}
                    message={snackbar.message}
                />
            </Snackbar>

            {/* Hesapla Onay Diyalogu */}
            <Dialog open={calcConfirmOpen} onClose={() => setCalcConfirmOpen(false)} maxWidth="xs" fullWidth
                PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>

                {/* Başlık */}
                <Box sx={{ px: 3, pt: 2.5, pb: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <WarningAmberRoundedIcon sx={{ color: "#00695c", fontSize: 26 }} />
                    <Typography variant="h6" fontWeight={700} color={H_DARK}>
                        Hesaplama Başlatılacak
                    </Typography>
                </Box>
                <Divider />

                <DialogContent sx={{ pt: 2, pb: 1 }}>
                    <MuiAlert severity="warning" variant="outlined" sx={{ mb: 2.5, borderRadius: 2, fontSize: "0.82rem" }}>
                        Bu işlem <strong>{selectedYear}</strong> plan yılına ait hesaplanmış montaj adetleri
                        {" "}<strong>oluşturulacaktır.</strong>
                    </MuiAlert>

                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Devam etmeden önce kontrol edin
                    </Typography>

                    <Stack spacing={1} sx={{ mt: 1.5 }}>
                        {[
                            "Satış Adetleri yüklendi ve kaydedildi",
                            "Gerçekleşen Adetler yüklendi ve kaydedildi",
                            "Montaj Oranları yüklendi ve kaydedildi",
                            "Mevsimsel İndeksler yüklendi ve kaydedildi",
                        ].map((text) => (
                            <Stack key={text} direction="row" spacing={1} alignItems="center">
                                <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "#00695c", flexShrink: 0 }} />
                                <Typography variant="body2">{text}</Typography>
                            </Stack>
                        ))}
                        <Stack direction="row" spacing={1} alignItems="center">
                            <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "#00695c", flexShrink: 0 }} />
                            <Typography variant="body2">
                                Seçilen plan yılı:
                                <Box component="span" sx={{ fontWeight: 700, color: "#00695c", bgcolor: "#e0f2f1", px: 0.75, py: 0.2, borderRadius: 1 }}>
                                    {selectedYear}
                                </Box>
                            </Typography>
                        </Stack>
                    </Stack>
                </DialogContent>

                <Divider sx={{ mt: 2 }} />
                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <Button variant="outlined" onClick={() => setCalcConfirmOpen(false)} sx={{ fontWeight: 600, borderRadius: 2 }}>
                        İptal
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCalculate}
                        startIcon={<PlayCircleOutlineIcon />}
                        style={{ backgroundColor: "#00695c", color: "#fff" }}
                        sx={{ fontWeight: 700, borderRadius: 2 }}
                    >
                        Evet, Hesaplamayı Başlat
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Hesaplama Sonuç Popup'ı */}
            <Dialog open={resultModalOpen} onClose={() => setResultModalOpen(false)} maxWidth="lg" fullWidth
                PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>

                <Box sx={{ px: 3, pt: 2.5, pb: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <TaskAltIcon sx={{ color: "#00695c", fontSize: 28 }} />
                    <Typography variant="h6" fontWeight={700} color={H_DARK}>
                        Hesaplama İşlemi Tamamlandı
                    </Typography>
                </Box>
                <Divider />

                <DialogContent sx={{ pt: 2.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Hesaplama işlemleri başarıyla tamamlanmıştır. Oluşan veriler aşağıda özet olarak gösterilmektedir.
                    </Typography>

                    {/* SAP-BPC Oranları */}
                    <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden", mb: 3 }}>
                        <Box sx={{ px: 2, py: 1.5, bgcolor: "#ffebee", display: "flex", alignItems: "center", gap: 1 }}>
                            <PercentIcon sx={{ color: "#c62828", fontSize: 20 }} />
                            <Typography variant="subtitle2" fontWeight={700} color="#c62828">SAP-BPC Oranları</Typography>
                            <Chip size="small" label={`${sapBpcRows.length} kayıt`} sx={{ ml: "auto" }} />
                        </Box>
                        <PreviewTable columns={SAP_BPC_RATE_COLS} rows={sapBpcRows} disableSticky />
                    </Paper>

                    {/* Montaj Birim Maliyeti - SAP-BPC'den görsel olarak ayrıştırılmış ayrı kart */}
                    <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
                        <Box sx={{ px: 2, py: 1.5, bgcolor: "#e0f7fa", display: "flex", alignItems: "center", gap: 1 }}>
                            <ReceiptLongOutlinedIcon sx={{ color: "#00838f", fontSize: 20 }} />
                            <Typography variant="subtitle2" fontWeight={700} color="#00838f">Montaj Birim Maliyeti</Typography>
                            <Chip size="small" label={`${unitCostCalcRows.length} kayıt`} sx={{ ml: "auto" }} />
                        </Box>
                        <PreviewTable columns={UNIT_COST_SUMMARY_COLS} rows={unitCostCalcRows} disableSticky />
                    </Paper>

                    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 3, p: 1.5, bgcolor: "#f5f5f5", borderRadius: 2 }}>
                        <InfoOutlinedIcon sx={{ fontSize: 18, color: "text.secondary", mt: "1px" }} />
                        <Typography variant="caption" color="text.secondary">
                            Not: Hesaplanan verilere daha sonra ilgili sekmeler (Tabs) üzerinden de ulaşabilir ve detaylı olarak sorgulayabilirsiniz.
                        </Typography>
                    </Stack>
                </DialogContent>

                <Divider />
                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <Button variant="outlined" onClick={() => setResultModalOpen(false)} sx={{ fontWeight: 600, borderRadius: 2 }}>
                        Kapat
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleExportResultsToExcel}
                        startIcon={<FileDownloadOutlinedIcon />}
                        style={{ backgroundColor: "#00695c", color: "#fff" }}
                        sx={{ fontWeight: 700, borderRadius: 2 }}
                    >
                        Excel'e Aktar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}